import {
  TriangleDownSmallIcon,
  TriangleRightSmallIcon,
  BinIcon,
  PencilIcon,
} from '../../assets/icons';
import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { GrowegyMenu } from './GrowegyMenu';
import { ConfirmModal } from './ConfirmModal';
import _ from 'lodash';
import { INTERPUNCT_CHARACTER } from '../constants';
import { StyleVariables } from '../styleConstants';

type GroupedItems<T> = { key: string; items: T[]; isDummyGroup: boolean }[];
type TempGroupedItems<T> = Record<string, { order: number; items: T[]; isDummyGroup: boolean }>;
const groupBy = <T,>(array: T[], keyGetter: (item: T) => string | null): GroupedItems<T> => {
  let i = 0;
  return _.entries(
    array.reduce((acc: TempGroupedItems<T>, item: T) => {
      const groupKey = keyGetter(item);
      const key = {
        value: groupKey ?? `${DUMMY_CATEGORY_PREFIX}-${i}`,
        isDummy: groupKey === null,
      };

      // Check if the group already exists, if not, create an empty array for the group
      if (!acc[key.value] || key.isDummy) {
        acc[key.value] = { order: i, items: [], isDummyGroup: key.isDummy };
        i += 1;
      }

      // Push the current item into the corresponding group
      acc[key.value].items.push(item);

      return acc;
    }, {})
  )
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, value]) => ({ key, items: value.items, isDummyGroup: value.isDummyGroup }));
};

export type GroupItemProps = PropsWithChildren<{
  item: string;
  total: number;
  tickedCount?: number;
  containsSelectedItem: boolean;
}>;

const GroupItem = (props: GroupItemProps) => {
  const { item, total, tickedCount, containsSelectedItem } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => {
    if (containsSelectedItem) setIsExpanded(true);
  }, [containsSelectedItem]);

  return (
    <>
      <Item
        item={item}
        isSelected={false}
        onItemSelected={() => setIsExpanded((x) => !x)}
        nameGetter={(x) => x}
        info={tickedCount !== undefined ? `${tickedCount} / ${total}` : `${total}`}
        iconComponent={({ className }) =>
          isExpanded ? (
            <TriangleDownSmallIcon
              className={className}
              style={{ fill: StyleVariables.neutral500 }}
            />
          ) : (
            <TriangleRightSmallIcon
              className={className}
              style={{ fill: StyleVariables.neutral500 }}
            />
          )
        }
        noMenu={true}
      />
      <div style={{ marginLeft: 16 }}>{isExpanded && props.children}</div>
    </>
  );
};

type ItemProps<T> = {
  item: T;
  isSelected: boolean;
  onItemSelected: (item: T | null) => void;
  nameGetter: (item: T) => string;
  info?: string;
  iconComponent?: (props: { item: T; className: string }) => ReactElement | null;
  onDeleteItem?: (item: T) => void;
  onRenameItem?: (item: T, newName: string) => void;
  noMenu?: boolean;
  noIcon?: boolean;
  groupKey?: string | null;
};
const Item = <T,>(props: ItemProps<T>) => {
  const {
    item,
    isSelected,
    onItemSelected,
    nameGetter,
    info,
    iconComponent: IconComponent,
    onDeleteItem,
    onRenameItem,
    noMenu,
    noIcon,
    groupKey,
  } = props;

  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const name = nameGetter(item);
  useEffect(() => {
    if (!isEditing || !nameInputRef.current) return;
    nameInputRef.current.value = name;
    nameInputRef.current.focus();
    nameInputRef.current.selectionStart = 0;
    nameInputRef.current.selectionEnd = nameInputRef.current.value.length;
  }, [isEditing, name]);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const onItemSelectedCallback = useCallback(() => onItemSelected(item), [item, onItemSelected]);

  return (
    <>
      <div
        className={clsx('item-list__item-container', {
          'item-list__item-container--selected': isSelected,
        })}
        data-test="item-list__item-container"
        onClick={onItemSelectedCallback}
      >
        {!noIcon && (
          <div className="item-list__item-icon-box">
            {IconComponent && <IconComponent item={item} className="flex-shrink-0" />}
          </div>
        )}
        <div className="item-list__item-name">
          {isEditing && onRenameItem ? (
            <input
              ref={nameInputRef}
              type="text"
              maxLength={100}
              className="growegy-form__input w-100"
              placeholder="Enter name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newName = (e.target as HTMLInputElement).value;
                  if (!newName) return;
                  onRenameItem(item, newName);
                  setIsEditing(false);
                } else if (e.key === 'Escape') {
                  setIsEditing(false);
                }
              }}
              onBlur={() => {
                setIsEditing(false);
              }}
              data-test="item-list__item-name-input"
            />
          ) : (
            <div className="item-list__item-name-container">
              <div
                className="text-truncate"
                data-test={`item-list__item-name-label${
                  groupKey ? `--${groupKey.replaceAll(' ', '-')}` : ''
                }`}
              >
                {name}
              </div>
              {info && (
                <>
                  <div className="item-list__item-name-info">{INTERPUNCT_CHARACTER}</div>
                  <div className="item-list__item-name-info">{info}</div>
                </>
              )}
            </div>
          )}
        </div>
        {!isEditing && !noMenu && (
          <GrowegyMenu
            menuItems={[
              {
                type: 'standard',
                text: 'Rename',
                icon: <PencilIcon />,
                dataTest: 'item-list__item-rename',
                onClick: () => setIsEditing(true),
                isHidden: !onRenameItem,
              },
              {
                type: 'standard',
                text: 'Delete',
                icon: <BinIcon />,
                dataTest: 'item-list__item-delete',
                onClick: () => setShowDeleteConfirmation(true),
                isHidden: !onDeleteItem,
              },
            ]}
            menuButtonDataTest="chat-list__item-menu-button"
            menuDataTest="chat-list__item-menu"
          />
        )}
      </div>
      {showDeleteConfirmation && onDeleteItem && (
        <ConfirmModal
          title={`Delete the “${name}”?`}
          message="You won’t be able to restore it."
          footer={{
            type: 'show',
            applyButton: {
              label: 'Delete',
              variant: 'danger',
              onClick: () => {
                setShowDeleteConfirmation(false);
                onDeleteItem(item);
              },
            },
            cancelButton: {
              label: 'Cancel',
              onClick: () => {
                setShowDeleteConfirmation(false);
              },
            },
          }}
        />
      )}
    </>
  );
};

const DUMMY_CATEGORY_PREFIX = 'dummy-category';

export const ItemList = <T,>(props: {
  items: T[] | null;
  itemsLoadingComponent: () => ReactElement;
  selectedItem: T | null;
  onItemSelected: (item: T | null) => void;
  idGetter: (item: T) => string;
  nameGetter: (item: T) => string;
  iconComponent?: (props: { item: T; className: string }) => ReactElement | null;
  categoryGetter?: (item: T) => string | null;
  tickedGetter?: (item: T) => boolean;
  noMenu?: boolean;
  noIcons?: boolean;
  groupItemComponent?: (props: GroupItemProps) => ReactElement;
}) => {
  const {
    items,
    itemsLoadingComponent: ItemsLoadingComponent,
    selectedItem,
    onItemSelected,
    idGetter,
    nameGetter,
    iconComponent,
    categoryGetter,
    tickedGetter,
    noMenu,
    noIcons,
    groupItemComponent,
  } = props;

  const groupedItems = items
    ? groupBy(items, (item) => (categoryGetter && categoryGetter(item)) ?? null)
    : null;

  const EffectiveGroupItem: (props: GroupItemProps) => ReactElement =
    groupItemComponent ?? GroupItem;
  return (
    <div className="chat-list__container">
      <>
        {groupedItems ? (
          groupedItems.length > 0 && (
            <div className="chat-list__items-with-header">
              <div className="chat-list__items">
                {groupedItems.map((group) =>
                  group.isDummyGroup ? (
                    group.items.map((x) => (
                      <Item<T>
                        key={idGetter(x)}
                        item={x}
                        isSelected={selectedItem != null && idGetter(selectedItem) === idGetter(x)}
                        onItemSelected={onItemSelected}
                        nameGetter={nameGetter}
                        iconComponent={iconComponent}
                        noMenu={noMenu}
                        noIcon={noIcons}
                      />
                    ))
                  ) : (
                    <EffectiveGroupItem
                      item={group.key}
                      key={group.key}
                      total={group.items.length}
                      tickedCount={
                        tickedGetter ? group.items.filter(tickedGetter).length : undefined
                      }
                      containsSelectedItem={group.items.some(
                        (item) => selectedItem && idGetter(item) === idGetter(selectedItem)
                      )}
                    >
                      <>
                        {group.items.map((x) => (
                          <Item<T>
                            key={idGetter(x)}
                            item={x}
                            isSelected={
                              selectedItem != null && idGetter(selectedItem) === idGetter(x)
                            }
                            onItemSelected={onItemSelected}
                            nameGetter={nameGetter}
                            iconComponent={iconComponent}
                            noMenu={noMenu}
                            noIcon={noIcons}
                            groupKey={group.key}
                          />
                        ))}
                      </>
                    </EffectiveGroupItem>
                  )
                )}
              </div>
            </div>
          )
        ) : (
          <ItemsLoadingComponent />
        )}
      </>
    </div>
  );
};
